/**
 * @fileoverview home-util.ts — pure helpers for the Projects-home blocks.
 * @module apps/network/consumer/src/home/home-util
 *
 * Zero-effect projections over the identity project list + the deployment
 * event stream: per-project status rollups, a friendly account-wide activity
 * feed, headline stats, and the deterministic gradient/letter-mark a project
 * gets in lieu of a real thumbnail. No IO, no side effects — easy to test.
 */

import type { DeploymentStreamEvent } from "../../../global/io/nodes/deployment/deployment-io.js";
import { stageFromOp } from "../features/deployments/release-event-map.js";

/** The structural project shape the home blocks read (face-agnostic). */
export interface ProjectLike {
  readonly id: string;
  readonly name: string;
  readonly productionDomain?: string;
  readonly createdAtMs: number;
}

export type ProjectStatus = "live" | "building" | "error" | "idle";

export interface ProjectRollup {
  readonly status: ProjectStatus;
  readonly lastActivityMs: number;
}

/** Pull a project id (topology target) from an event's detail, if present. */
export function eventProjectId(e: DeploymentStreamEvent): string | null {
  if (!e.detail || typeof e.detail !== "object") return null;
  const v = (e.detail as Record<string, unknown>)["targetId"];
  return typeof v === "string" ? v : null;
}

/** Fold the event stream into per-project {status, lastActivity}. Latest event
 *  wins. Mirrors the rollup the per-project cards already use. */
export function projectRollup(
  events: readonly DeploymentStreamEvent[],
): Map<string, ProjectRollup> {
  const map = new Map<string, ProjectRollup>();
  for (const e of events) {
    const pid = eventProjectId(e);
    if (!pid) continue;
    const stage = stageFromOp(e.op);
    let status: ProjectStatus = "idle";
    if (e.origin === "runtime" || e.origin === "domain") status = "error";
    else if (
      stage === "running" || stage === "cutover" || stage === "drained" ||
      stage === "previewing" || stage === "gated"
    ) status = "building";
    else if (stage === "succeeded") status = "live";
    else if (e.op.startsWith("rollback.")) status = "live";
    const prev = map.get(pid);
    if (!prev || e.atMs > prev.lastActivityMs) {
      map.set(pid, { status, lastActivityMs: e.atMs });
    }
  }
  return map;
}

/** Compact relative time ("just now", "5m ago", "2h ago", "3d ago"). */
export function relTime(ms?: number): string {
  if (!ms) return "—";
  const d = Date.now() - ms;
  if (d < 60_000) return "just now";
  if (d < 3_600_000) return `${Math.floor(d / 60_000)}m ago`;
  if (d < 86_400_000) return `${Math.floor(d / 3_600_000)}h ago`;
  return `${Math.floor(d / 86_400_000)}d ago`;
}

/** Deterministic two-stop gradient for a project "cover" — stable per id,
 *  so a project always wears the same colours (no thumbnail needed). */
export function projectGradient(id: string): string {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const hue = (h >>> 0) % 360;
  const hue2 = (hue + 36) % 360;
  return `linear-gradient(140deg, hsl(${hue} 48% 30%) 0%, hsl(${hue2} 42% 18%) 100%)`;
}

/** First letter of a name, uppercased (the letter-mark). */
export function initial(name: string): string {
  return (name.trim().charAt(0) || "?").toUpperCase();
}

/** v1: every project is a deployable App. A real `type` field
 *  (App / Site / Page / Service) lands with the project data model. */
export function projectTypeLabel(_p: ProjectLike): string {
  return "App";
}

export type FeedTone = "ok" | "info" | "warn" | "error";

export interface FeedEntry {
  readonly id: string;
  readonly pid: string | null;
  readonly label: string;
  readonly atMs: number;
  readonly tone: FeedTone;
}

const STAGE_FEED_LABEL: Record<string, string> = {
  previewing: "Preview started",
  gated: "Gates checked",
  running: "Provisioning",
  cutover: "Cut over to new version",
  drained: "Drained previous version",
  succeeded: "Deploy succeeded",
};

/** A human one-liner for an event — coarse stage names + a few specific ops. */
export function feedLabel(e: DeploymentStreamEvent): string {
  const stage = stageFromOp(e.op);
  if (stage) return STAGE_FEED_LABEL[stage] ?? stage;
  if (e.op.startsWith("build.")) {
    const blob = `${e.op} ${e.code}`.toLowerCase();
    if (blob.includes("succeed")) return "Build succeeded";
    if (blob.includes("fail")) return "Build failed";
    if (e.op === "build.start") return "Build started";
    return "Build update";
  }
  if (e.op.startsWith("workspace.git")) return "Committed changes";
  if (e.op.startsWith("rollback.")) return "Rolled back";
  const tail = e.op.replace(/^[a-z]+\./, "").replace(/\./g, " ");
  return tail.charAt(0).toUpperCase() + tail.slice(1);
}

function feedTone(e: DeploymentStreamEvent): FeedTone {
  if (e.origin === "runtime" || e.origin === "domain") return "error";
  const blob = `${e.op} ${e.code}`.toLowerCase();
  if (/fail|error|denied|reject/.test(blob)) return "error";
  if (stageFromOp(e.op) === "succeeded" || /succeed|healthy|\bok\b/.test(blob)) return "ok";
  return "info";
}

/** The account-wide activity feed — newest first, friendly, capped. */
export function feedEntries(
  events: readonly DeploymentStreamEvent[],
  limit = 14,
): FeedEntry[] {
  const out: FeedEntry[] = [];
  for (let i = events.length - 1; i >= 0 && out.length < limit; i--) {
    const e = events[i];
    if (!e) continue;
    out.push({
      id: `${e.atMs}:${i}`,
      pid: eventProjectId(e),
      label: feedLabel(e),
      atMs: e.atMs,
      tone: feedTone(e),
    });
  }
  return out;
}

export interface AccountStats {
  readonly projects: number;
  readonly live: number;
  readonly deploys24h: number;
  readonly issues: number;
}

/** Headline numbers for the stat strip. */
export function accountStats(
  projects: readonly ProjectLike[],
  events: readonly DeploymentStreamEvent[],
  rollup: Map<string, ProjectRollup>,
): AccountStats {
  let live = 0;
  let issues = 0;
  for (const r of rollup.values()) {
    if (r.status === "live") live++;
    if (r.status === "error") issues++;
  }
  const since = Date.now() - 86_400_000;
  let deploys24h = 0;
  for (const e of events) {
    if (e.atMs >= since && stageFromOp(e.op) === "succeeded") deploys24h++;
  }
  return { projects: projects.length, live, deploys24h, issues };
}

/** Time-of-day greeting. */
export function greeting(d = new Date()): string {
  const h = d.getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

/** "Sunday, May 25" — the hero's date line. */
export function longDate(d = new Date()): string {
  return d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
}
